import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { api } from "../../lib/api";

const DEFAULT_FORM = {
  user_id: "",
  voucher_type: "FLAT",
  voucher_value: "",
  min_booking_amount: "",
  trip_type: "ANY",
  passenger_type: "ANY",
  airlines: "",
  sectors: "",
  max_usage: 1,
  notes: "",
};

const debounce = (fn, t = 350) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), t);
  };
};

const extractAirportCode = (text) => {
  if (!text) return "";
  const t = String(text).trim().toUpperCase();

  const match = t.match(/\(([A-Z0-9]{3,4})\)/);
  if (match?.[1]) return match[1];

  if (/^[A-Z0-9]{3,4}$/.test(t)) return t;

  return "";
};

// Score airport for sorting — higher score = shown first
const getAirportScore = (a, q) => {
  const code = (a.airport_code || "").toLowerCase();
  const city = (a.city_name || "").toLowerCase();
  const isIndia =
    (a.country_code || "").toUpperCase() === "IN" ||
    (a.country_name || "").toLowerCase() === "india";

  if (isIndia && code === q) return 7;
  if (isIndia && city === q) return 6;
  if (isIndia && city.startsWith(q)) return 5;
  if (isIndia && city.includes(q)) return 4;
  if (city === q) return 3;
  if (city.startsWith(q)) return 2;
  if (city.includes(q)) return 1;
  if (code === q) return 0;
  return -1;
};

export default function CourtesyVoucherUpsertModal({ open, onClose, onCreate, loading }) {
  const [form, setForm] = useState(DEFAULT_FORM);

  const [userQuery, setUserQuery] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const boxRef = useRef(null);

  const [airports, setAirports] = useState([]);
  const [sectorPairs, setSectorPairs] = useState([]);

  const [sectorFromQuery, setSectorFromQuery] = useState("");
  const [sectorFromResults, setSectorFromResults] = useState([]);
  const [sectorFromOpen, setSectorFromOpen] = useState(false);

  const [sectorToQuery, setSectorToQuery] = useState("");
  const [sectorToResults, setSectorToResults] = useState([]);
  const [sectorToOpen, setSectorToOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(DEFAULT_FORM);
    setUserQuery("");
    setSelectedUser(null);
    setUserOptions([]);
    setSectorPairs([]);
    setSectorFromQuery("");
    setSectorToQuery("");
    setSectorFromResults([]);
    setSectorToResults([]);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const loadAirports = async () => {
      try {
        const res = await fetch("/airports.json");
        const json = await res.json();
        if (Array.isArray(json)) setAirports(json);
      } catch (e) {
        console.error("Failed to load airports.json", e);
      }
    };
    loadAirports();
  }, [open]);

  useEffect(() => {
    if (!sectorFromOpen || !sectorFromQuery.trim()) {
      setSectorFromResults([]);
      return;
    }
    const q = sectorFromQuery.trim().toLowerCase();
    setSectorFromResults(
      airports
        .filter((a) => {
          const name = (a.airport_name || "").toLowerCase();
          const code = (a.airport_code || "").toLowerCase();
          const city = (a.city_name || "").toLowerCase();
          return name.includes(q) || code.includes(q) || city.includes(q);
        })
        .sort((a, b) => getAirportScore(b, q) - getAirportScore(a, q))
        .slice(0, 10)
    );
  }, [sectorFromQuery, airports, sectorFromOpen]);

  useEffect(() => {
    if (!sectorToOpen || !sectorToQuery.trim()) {
      setSectorToResults([]);
      return;
    }
    const q = sectorToQuery.trim().toLowerCase();
    setSectorToResults(
      airports
        .filter((a) => {
          const name = (a.airport_name || "").toLowerCase();
          const code = (a.airport_code || "").toLowerCase();
          const city = (a.city_name || "").toLowerCase();
          return name.includes(q) || code.includes(q) || city.includes(q);
        })
        .sort((a, b) => getAirportScore(b, q) - getAirportScore(a, q))
        .slice(0, 10)
    );
  }, [sectorToQuery, airports, sectorToOpen]);

  useEffect(() => {
    setForm((s) => ({ ...s, sectors: sectorPairs.join(",") }));
  }, [sectorPairs]);

  const addSectorPair = () => {
    const from = extractAirportCode(sectorFromQuery);
    const to = extractAirportCode(sectorToQuery);
    if (!from || !to) return toast.error("Pick both a From and To airport");
    if (from === to) return toast.error("From and To can't be the same airport");
    const pair = `${from}-${to}`;
    if (sectorPairs.includes(pair)) return toast.error("Sector already added");
    setSectorPairs((s) => [...s, pair]);
    setSectorFromQuery("");
    setSectorToQuery("");
    setSectorFromOpen(false);
    setSectorToOpen(false);
  };

  const removeSectorPair = (pair) => {
    setSectorPairs((s) => s.filter((p) => p !== pair));
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setUserOptions([]);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const parseAirlines = () =>
    (form.airlines || "")
      .split(",")
      .map((x) => x.trim().toUpperCase())
      .filter(Boolean);

  const parseSectors = () =>
    (form.sectors || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean)
      .map((pair) => {
        const [from, to] = pair.split("-").map((y) => (y || "").trim().toUpperCase());
        return { from, to };
      })
      .filter((s) => s.from && s.to);

  const validate = () => {
    if (!form.user_id) return "Please choose a user";
    const v = Number(form.voucher_value);
    if (!form.voucher_value || Number.isNaN(v) || v <= 0) return "Voucher amount must be > 0";
    if (sectorPairs.length === 0) return "At least one sector (e.g. MAA-DEL) is required";
    return null;
  };

  const fetchUsers = debounce(async (q) => {
    try {
      setUserLoading(true);
      const res = await api.get("/admin/users", {
        params: { mode: "picker", search: q, page: 1, limit: 10 },
      });
      setUserOptions(res.data?.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load users");
    } finally {
      setUserLoading(false);
    }
  });

  const onUserInput = (e) => {
    const q = e.target.value;
    setUserQuery(q);
    setSelectedUser(null);
    setForm((s) => ({ ...s, user_id: "" }));
    if (q.trim().length >= 2) fetchUsers(q.trim());
    else setUserOptions([]);
  };

  const pickUser = (u) => {
    setSelectedUser(u);
    setUserQuery(`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || u.mobile_no || u.user_id);
    setUserOptions([]);
    setForm((s) => ({ ...s, user_id: u.user_id }));
  };

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);

    const payload = {
      user_id: form.user_id,
      voucher_type: form.voucher_type, 
      voucher_value: Number(form.voucher_value),
      min_booking_amount: form.min_booking_amount ? Number(form.min_booking_amount) : null,
      trip_type: form.trip_type,
      passenger_type: form.passenger_type,
      airlines: parseAirlines(),
      sectors: parseSectors(),
      max_usage: Number(form.max_usage) || 1,
      notes: form.notes || null,
    };

    try {
      const res = await onCreate(payload);
      toast.success(res?.data?.message || "Voucher created");
      onClose();
    } catch (e2) {
      toast.error(e2?.response?.data?.message || e2.message || "Create failed");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/30 grid place-items-center p-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl border p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="text-lg font-bold">Create Courtesy Voucher</div>
          <button onClick={onClose} className="text-sm px-3 py-1 rounded-lg hover:bg-gray-100">
            Close
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* USER PICKER */}
            <div className="md:col-span-2" ref={boxRef}>
              <label className="text-sm font-semibold">Choose User</label>
              <input
                className="mt-2 w-full border rounded-xl px-3 py-2"
                placeholder="Type name / email / mobile (min 2 chars)"
                value={userQuery}
                onChange={onUserInput}
              />
              {userLoading ? <div className="text-xs text-gray-500 mt-1">Searching...</div> : null}

              {userOptions.length > 0 ? (
                <div className="mt-2 border rounded-xl overflow-hidden max-h-56 overflow-y-auto">
                  {userOptions.map((u) => (
                    <button
                      type="button"
                      key={u.user_id}
                      onClick={() => pickUser(u)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b last:border-b-0"
                    >
                      <div className="text-sm font-semibold">
                        {(u.first_name || "") + " " + (u.last_name || "")}
                      </div>
                      <div className="text-xs text-gray-600">
                        {u.email || "-"} • {u.mobile_no || "-"} • {u.user_id}
                      </div>
                    </button>
                  ))}
                </div>
              ) : null}

              {selectedUser ? (
                <div className="text-xs text-green-700 mt-2">
                  Selected: <b>{selectedUser.email || selectedUser.mobile_no}</b> ({selectedUser.user_id})
                </div>
              ) : null}
            </div>

            <div>
              <label className="text-sm font-semibold">Voucher Type</label>
              <select className="mt-2 w-full border rounded-xl px-3 py-2" value={form.voucher_type} onChange={onChange("voucher_type")}>
                <option value="FLAT">FLAT</option>
                {/* <option value="PERCENT">PERCENT</option> */}. 
              </select>
              <p className="text-xs text-gray-500 mt-1">You said “amount only”; keep FLAT for now.</p>
            </div>

            <div>
              <label className="text-sm font-semibold">Voucher Amount</label>
              <input type="number" min={1} className="mt-2 w-full border rounded-xl px-3 py-2" value={form.voucher_value} onChange={onChange("voucher_value")} />
            </div>

            <div>
              <label className="text-sm font-semibold">Min Booking Amount (optional)</label>
              <input type="number" min={0} className="mt-2 w-full border rounded-xl px-3 py-2" value={form.min_booking_amount} onChange={onChange("min_booking_amount")} />
            </div>

            <div>
              <label className="text-sm font-semibold">Max Usage</label>
              <input type="number" min={1} className="mt-2 w-full border rounded-xl px-3 py-2" value={form.max_usage} onChange={onChange("max_usage")} />
            </div>

            <div>
              <label className="text-sm font-semibold">Trip Type</label>
              <select className="mt-2 w-full border rounded-xl px-3 py-2" value={form.trip_type} onChange={onChange("trip_type")}>
                <option value="ANY">ANY</option>
                <option value="ONE_WAY">ONE_WAY</option>
                <option value="RETURN">RETURN</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-semibold">Passenger Type</label>
              <select className="mt-2 w-full border rounded-xl px-3 py-2" value={form.passenger_type} onChange={onChange("passenger_type")}>
                <option value="ANY">ANY</option>
                <option value="SINGLE">SINGLE</option>
                <option value="MULTIPLE">MULTIPLE</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-semibold">Airlines (comma)</label>
              <input className="mt-2 w-full border rounded-xl px-3 py-2" placeholder="AI,6E" value={form.airlines} onChange={onChange("airlines")} />
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-semibold">Sectors</label>

              <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 mt-2">
                <div className="relative">
                  <input
                    className="w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                    placeholder="From city / airport"
                    value={sectorFromQuery}
                    onFocus={() => setSectorFromOpen(true)}
                    onBlur={() => setTimeout(() => setSectorFromOpen(false), 150)}
                    onChange={(e) => {
                      setSectorFromQuery(e.target.value);
                      setSectorFromOpen(true);
                    }}
                  />
                  {sectorFromOpen && sectorFromResults.length > 0 ? (
                    <div className="absolute z-50 mt-1 w-full bg-white border rounded-xl shadow max-h-56 overflow-auto">
                      {sectorFromResults.map((a) => (
                        <button
                          key={a.airport_code}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setSectorFromQuery(`${a.city_name || a.airport_name} (${a.airport_code})`);
                            setSectorFromOpen(false);
                          }}
                        >
                          {a.city_name || a.airport_name} ({a.airport_code})
                          <span className="text-gray-500"> - {a.airport_name}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                <div className="relative">
                  <input
                    className="w-full border rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-black"
                    placeholder="To city / airport"
                    value={sectorToQuery}
                    onFocus={() => setSectorToOpen(true)}
                    onBlur={() => setTimeout(() => setSectorToOpen(false), 150)}
                    onChange={(e) => {
                      setSectorToQuery(e.target.value);
                      setSectorToOpen(true);
                    }}
                  />
                  {sectorToOpen && sectorToResults.length > 0 ? (
                    <div className="absolute z-50 mt-1 w-full bg-white border rounded-xl shadow max-h-56 overflow-auto">
                      {sectorToResults.map((a) => (
                        <button
                          key={a.airport_code}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setSectorToQuery(`${a.city_name || a.airport_name} (${a.airport_code})`);
                            setSectorToOpen(false);
                          }}
                        >
                          {a.city_name || a.airport_name} ({a.airport_code})
                          <span className="text-gray-500"> - {a.airport_name}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                <button
                  type="button"
                  onClick={addSectorPair}
                  className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50 whitespace-nowrap"
                >
                  + Add Sector
                </button>
              </div>

              {sectorPairs.length > 0 ? (
                <div className="flex flex-wrap gap-2 mt-3">
                  {sectorPairs.map((pair) => (
                    <span key={pair} className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold">
                      {pair}
                      <button
                        type="button"
                        onClick={() => removeSectorPair(pair)}
                        className="text-gray-500 hover:text-red-600"
                        aria-label={`Remove ${pair}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 mt-2">No sectors added yet. Pick a From and To airport, then Add Sector.</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="text-sm font-semibold">Notes (internal)</label>
              <textarea className="mt-2 w-full border rounded-xl px-3 py-2 min-h-[80px]" value={form.notes} onChange={onChange("notes")} />
              <p className="text-xs text-gray-500 mt-1">valid_till will be auto: today + 365 days.</p>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="rounded-xl bg-black px-4 py-2 text-white text-sm font-semibold disabled:opacity-60">
              {loading ? "Saving..." : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
